import React from "react";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import type { UserLookup } from "@/hooks/use-users";
import type { LoggedEntryRef } from "@/hooks/use-budget-line-logged-hours";
import { isTaskLine } from "@/lib/billing-calculations";
import { PHASE_LABELS, type ProjectPhase } from "@/lib/offer-types";
import { cn } from "@/lib/utils";
import { statusFor, STATUS_TEXT } from "@/lib/budget-status";
import { BudgetLineRowComponent } from "./BudgetTab";
import { BudgetHoursBar } from "./BudgetHoursBar";
import { BulkAssignPhaseButton } from "./BulkAssignPhaseButton";

/**
 * VIB-723: "annet" tillater rendering av task-linjer med ukjent eller tom
 * phase, så de ikke forsvinner fra Timetildeling-tab uten varsel.
 * VIB-995: "tilvalg" samler opsjons-linjer fra tilbudet i egen rubrikk.
 */
export type PhaseGroupKey = ProjectPhase | "annet" | "tilvalg" | "mersalg";

const PHASE_GROUP_LABELS: Record<PhaseGroupKey, string> = {
  ...PHASE_LABELS,
  annet: "Annet",
  tilvalg: "Tilvalg",
  mersalg: "Mersalg",
};

interface BudgetPhaseGroupProps {
  phase: PhaseGroupKey;
  /** VIB-1151: overstyrer header-tittelen (brukes for per-mersalg-rubrikker
   *  der tittelen er mersalgets `description`, ikke en fast fase-etikett). */
  titleOverride?: string;
  lines: BudgetLineRow[];
  phaseIdx: number;
  /**
   * VIB-1230: dropp den løpende fase-nummereringen (01, 02, …) — brukt for
   * fase-undergrupper INNI en Tilvalg-/Mersalg-rubrikk, der rubrikk-tittelen
   * allerede bærer identiteten og et tall bare ville forvirret (hvilken
   * rekkefølge teller tallet?). Hovedbudsjettets fase-grupper beholder tallet.
   */
  hideIndex?: boolean;
  projectId: string;
  users: UserLookup[];
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  readOnly: boolean;
  loggedHoursTotals: Map<string, number>;
  loggedHoursPerUser: Map<string, Map<string, number>>;
  loggedHoursPerAssignment: Map<string, Map<string, number>>;
  /** VIB-1201: budget_line_id → (user_id → underliggende føringer), for hover-tooltip. */
  entriesPerUser: Map<string, Map<string, LoggedEntryRef[]>>;
  canAssignHours: boolean;
}

/**
 * En fase-gruppe i timetildeling-tabellen: header-bånd med fase-tall,
 * navn, oppgave-antall, tildelte vs. brukte timer, og progressbar, etterfulgt
 * av oppgaverader (BudgetLineRowComponent). Hopper over fasen helt hvis ingen
 * oppgavelinjer.
 */
export function BudgetPhaseGroup({
  phase,
  titleOverride,
  lines,
  phaseIdx,
  hideIndex = false,
  projectId,
  users,
  editingId,
  setEditingId,
  readOnly,
  loggedHoursTotals,
  loggedHoursPerUser,
  loggedHoursPerAssignment,
  entriesPerUser,
  canAssignHours,
}: BudgetPhaseGroupProps) {
  const taskLines = lines.filter(isTaskLine);
  if (taskLines.length === 0) return null;

  const phaseInternalLines = lines.filter((l) => l.cost_type === "internal");
  const phaseLoggedHours = phaseInternalLines.reduce(
    (s, l) => s + (loggedHoursTotals.get(l.id) ?? 0),
    0,
  );
  const phaseLoggedRound = Math.round(phaseLoggedHours * 10) / 10;
  const phaseInternalHours = phaseInternalLines.reduce(
    (s, l) => s + (l.quantity_hours ?? 0),
    0,
  );
  const phaseStatus = statusFor(phaseInternalHours, phaseLoggedHours);
  const phasePct = phaseInternalHours > 0
    ? Math.round((phaseLoggedHours / phaseInternalHours) * 100)
    : 0;
  const phaseNumber = String(phaseIdx + 1).padStart(2, "0");

  return (
    <React.Fragment>
      {/* Fase-bånd: lyst, dempet — tall + navn + bar + prosent.
          VIB-1230: inni en Tilvalg-/Mersalg-rubrikk (hideIndex) tones fase-
          båndet ytterligere ned så det ikke konkurrerer med rubrikk-tittelen
          rett over. */}
      <tr className={cn("border-y", hideIndex ? "bg-muted/15" : "bg-muted/40")}>
        <td className="px-2 py-3 text-center">
          {!hideIndex && (
            <span className="text-[11px] font-medium tabular-nums text-muted-foreground/80 font-display">
              {phaseNumber}
            </span>
          )}
        </td>
        <td className="px-2 py-3" colSpan={2}>
          <div className="flex items-center gap-2.5">
            <span
              className={cn(
                "font-display tracking-tight",
                hideIndex ? "font-normal text-[13px] text-muted-foreground" : "font-medium text-[15px]",
              )}
            >
              {titleOverride ?? PHASE_GROUP_LABELS[phase]}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {taskLines.length} oppgaver
            </span>
            {/* VIB-1207: bulk-tildel hele fasen til én person. */}
            {canAssignHours && !readOnly && (
              <BulkAssignPhaseButton
                projectId={projectId}
                users={users}
                lines={taskLines.map((l) => ({
                  id: l.id,
                  quantityHours: l.quantity_hours ?? 0,
                }))}
              />
            )}
          </div>
        </td>
        <td className="px-2 py-3 text-right tabular-nums font-medium text-muted-foreground">
          {phaseInternalHours}t
        </td>
        <td
          className={cn(
            "px-2 py-3 text-right tabular-nums font-semibold",
            STATUS_TEXT[phaseStatus],
          )}
        >
          {phaseLoggedRound}t
        </td>
        <td className="px-2 py-3">
          <div className="flex items-center gap-2">
            <BudgetHoursBar
              assigned={phaseInternalHours}
              used={phaseLoggedHours}
              height={5}
              className="flex-1"
            />
            <span className="text-[11px] text-muted-foreground tabular-nums w-9 text-right">
              {phasePct}%
            </span>
          </div>
        </td>
      </tr>

      {/* Oppgaver */}
      {taskLines.map((bl) => (
        <BudgetLineRowComponent
          key={bl.id}
          bl={bl}
          projectId={projectId}
          users={users}
          editingId={editingId}
          setEditingId={setEditingId}
          readOnly={readOnly}
          loggedHours={loggedHoursTotals.get(bl.id) ?? 0}
          loggedHoursPerUser={loggedHoursPerUser.get(bl.id)}
          loggedHoursPerAssignment={loggedHoursPerAssignment.get(bl.id)}
          entriesPerUser={entriesPerUser.get(bl.id)}
          canAssignHours={canAssignHours}
        />
      ))}
    </React.Fragment>
  );
}
